import { useQuery } from '@tanstack/react-query';
import { ediCatalogItemList, getEdiCatalogItemListQueryKey } from '@/api/hanly-v2/edi/edi';
import type {
  EdiCatalogItem,
  EdiCatalogItemPage,
  ItemDisplayColumn,
} from '@/api/hanly-v2/hanlyAPIV2.schemas';

/** API の page_size 上限 */
export const CATALOG_PAGE_SIZE = 500;

interface AllCatalogItems {
  results: EdiCatalogItem[];
  itemDisplayColumns: ItemDisplayColumn[] | undefined;
}

/** カタログを最終ページまで順に取得して結合する */
export const fetchAllCatalogItems = async (
  fetchPage: (page: number) => Promise<EdiCatalogItemPage>,
): Promise<AllCatalogItems> => {
  const first = await fetchPage(1);
  const results = [...first.results];
  const pageCount = Math.ceil(first.count / CATALOG_PAGE_SIZE);
  for (let page = 2; page <= pageCount; page++) {
    const next = await fetchPage(page);
    results.push(...next.results);
  }
  return {
    results,
    itemDisplayColumns: first.item_display_columns,
  };
};

/**
 * 発注画面はカタログの全商品を明細として持つため、ページを最後まで辿って全件取得する。
 * 先頭ページだけで打ち切ると、後続ページの商品が発注できず、再注文では「カタログに無い」と誤判定される。
 */
export const useAllCatalogItems = () =>
  useQuery({
    queryKey: [...getEdiCatalogItemListQueryKey({ page_size: CATALOG_PAGE_SIZE }), 'all-pages'],
    queryFn: ({ signal }) =>
      fetchAllCatalogItems((page) => ediCatalogItemList({ page, page_size: CATALOG_PAGE_SIZE }, { signal })),
  });
